import type { DataSchemaType } from "@/types/dataset";

/**
 * Where a number is being rendered. Surfaces exist so that a deliberate
 * difference between, say, the table and a chart is one reviewable line in
 * `FORMAT_CONFIG` rather than a divergence scattered across call sites.
 */
export type FormatSurface = "table" | "chart" | "summary";

/** How many decimals to show, how to round, and whether to keep trailing zeros. */
export type PrecisionRule = {
  /** Maximum decimals rendered. */
  fractionDigits?: number;
  /**
   * Minimum decimal places to display. Adds trailing zeros when needed.
   * Must be <= `fractionDigits`. Set both when overriding the defaults.
   */
  minFractionDigits?: number;
  /**
   * Round the last shown digit up rather than to nearest, so `0.241` reads
   * `0.25`. Only the table does this, and only because it always has.
   */
  roundUp?: boolean;
};

/**
 * The single source of truth for how numbers are displayed.
 *
 * Change a value here and every display that goes through `formatValue` /
 * `formatMeasure` follows. Nothing outside this module should hold a decimal
 * count of its own.
 *
 * The values below are the ones the app already used before this module
 * existed, so introducing it changes nothing on screen.
 *
 * It lives in `constants/` because that layer never imports from `utils/`,
 * which is exactly the property this config needs: `number.ts` reads it and
 * `format.ts` calls into `number.ts`, so a config placed among the utilities
 * could close that loop into an import cycle. The cycle would not merely warn —
 * the formatter cache is built while the module loads, so it would read a value
 * still in its temporal dead zone and throw on startup.
 */
export const FORMAT_CONFIG = {
  /** Display default for plain numbers. */
  fractionDigits: 3,

  /**
   * No trailing zeros for plain floats: `0.5` renders `0.5`, not `0.500`.
   * The per-type rules below opt in to trailing zeros via their own minimum.
   */
  minFractionDigits: 0,

  /**
   * Forced, never inherited from the browser. A bare `toLocaleString()` follows
   * the viewer's locale, which is why the widget table can disagree with the
   * chart beside it on an id-ID browser.
   */
  locale: "en-US",

  /**
   * Custom Breaks: the input limit, the rounding applied on save, and the
   * legend/panel display all read this one number, so it can be changed in a
   * single place.
   */
  customBreaks: 3,

  /**
   * Auto (quantile) color ranges: the decimals their boundaries are computed
   * and labelled at. A narrow-range attribute quantiles into boundaries that
   * only differ in the third decimal, so labelling them at fewer would make
   * adjacent ranges print identical text.
   */
  quantileBreaks: 3,

  /**
   * Round up by default, which is what the dataset table has always done.
   * Overstating every value is not defensible and the surfaces below already
   * disagree with it; unifying them is a visible change, so it is not made here.
   */
  roundUp: true,

  /**
   * The "(12.3%)" beside a chart value — a share of a total the chart computed,
   * not a value from the data. One decimal, always shown, no grouping.
   */
  chartShare: 1,

  /**
   * Precision of the dataset table's filter/sort keys. This is a matching key,
   * not a label: `formatNumberRaw` builds the strings the table's text filter
   * compares against, so changing this changes which rows a search matches. It
   * is deliberately independent of display precision.
   */
  filterKey: 3,

  /**
   * Per-surface overrides. An empty object means "follows the default".
   *
   * Charts and summaries round to nearest while the table rounds up: they
   * reached `Intl` through `toLocaleString`, which never rounded up, and the
   * table reached it through a formatter that did. Recording the split here is
   * what makes it visible; resolving it changes what people read.
   */
  surfaces: {
    table: {},
    chart: { roundUp: false },
    summary: { roundUp: false }
  } as Record<FormatSurface, PrecisionRule>,

  /**
   * The exception registry: the only sanctioned place to record a type that
   * deliberately differs from the default. Every entry carries its reason.
   *
   * Keyed by the schema type's wire value rather than the `DataSchemaType`
   * member, so the only import this file needs is the erased `import type`
   * above. The `Record` below still checks every key against the enum, so a
   * typo is a compile error.
   */
  types: {
    /** Whole numbers never show decimals. */
    int: { fractionDigits: 0, minFractionDigits: 0 },
    /** At 2 decimals a coordinate lands roughly 1.1 km away. */
    latitude: { fractionDigits: 6, minFractionDigits: 6 },
    longitude: { fractionDigits: 6, minFractionDigits: 6 },
    /** Trailing zeros kept so percentages line up. */
    percentage: { fractionDigits: 2, minFractionDigits: 2 },
    /** Product keeps 4 — a ratio needs the resolution. */
    ratio: { fractionDigits: 4, minFractionDigits: 4 }
  } as Partial<Record<DataSchemaType, PrecisionRule>>
};
